<script>
	import { nanoid } from "nanoid";

	import FormRow from "./FormRow.svelte";
	import FormColumn from "./FormColumn.svelte";

	export let name = null;
	export let label = null;
	export let sublabel = null;
	export let selected = [];
	export let optionList = [];
	export let required = null;
	export let disabled = null;
	export let validator = null;
	export let cols = 12;
	export let validatorValue = null;
	export let validatorMessage = null;
	// export let validatorTarget = null;
</script>

<FormRow>
	{#if label}
		<!-- svelte-ignore a11y-label-has-associated-control -->
		<label>
			{label}

			{#if required || validator}
				<span class="required">*</span>
			{/if}

			{#if sublabel}
				<div class="sublabel">{sublabel}</div>
			{/if}
		</label>
	{/if}

	{#each optionList as option, i (option)}
		{@const id = nanoid(4)}
		<FormColumn {cols} class="mb-2 checkbox-button-col">
			<input
				type="checkbox"
				on:change
				bind:group={selected}
				value={option.value}
				{id}
				{name}
				data-validator={validator}
				data-validator-value={validatorValue}
				data-bouncer-message={validatorMessage}
				data-bouncer-target={!!validatorValue ? `.${name}-error` : null}
				{required}
				{disabled} />
			<label for={id}>
				{@html option.label}
			</label>
		</FormColumn>
	{/each}
	{#if validatorValue}
		<div class="checkbox-list-error {name}-error"></div>
	{/if}
</FormRow>

<style>
	.checkbox-list-error {
		margin-left: 20px;
	}

	/* Ensure columns stretch to the same height if needed */
	:global(.checkbox-button-col) {
		display: flex; /* Make column a flex container */
		align-items: stretch; /* Make items inside stretch */
	}

	/* Base for label styling */
	[type="checkbox"]:not(:checked),
	[type="checkbox"]:checked {
		position: absolute;
		left: -9999px;
		opacity: 0; /* Hide but keep accessible */
	}

	[type="checkbox"]:not(:checked) + label,
	[type="checkbox"]:checked + label {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		text-align: center;
		width: 100%;
		min-height: 50px;
		height: 100%;
		border: 1px solid var(--color-border, #ccc);
		background: var(--color-white, #fff);
		border-radius: var(--border-radius, 1px);
		padding: var(--spacing-sm, 0.5rem 0.75rem);
		margin: 0;
		cursor: pointer;
		transition:
			border-color 0.2s ease,
			box-shadow 0.2s ease,
			background-color 0.2s ease;
		box-sizing: border-box;
	}

	:global(.invalid [type="checkbox"]:not(:checked) + label, .invalid [type="checkbox"]:checked + label) {
		border: solid 2px var(--color-error, red) !important;
	}

	[type="checkbox"]:focus-visible + label {
		/* Use focus-visible for better accessibility */
		outline: 2px solid var(--color-primary-focus, blue);
		outline-offset: 2px;
		/* text-decoration: underline; Removed in favor of outline */
	}

	[type="checkbox"]:checked + label {
		border-color: var(--color-primary, blue);
		color: var(--color-primary, blue) !important; /* Ensure override */
		box-shadow: inset 0 0 0 1px var(--color-primary, blue) !important; /* Ensure override */
		/* background-color: var(--color-primary-soft, #e6f2ff); */ /* Optional: slight background tint when checked */
	}

	/* Checkmark styling */
	[type="checkbox"]:checked + label:after,
	[type="checkbox"]:not(:checked) + label:after {
		content: "";
		position: absolute;
		height: 18px;
		width: 18px;
		border-radius: 50%;
		background-color: var(--color-primary, blue);
		z-index: 2;
		transition:
			top 0.2s ease,
			right 0.2s ease,
			opacity 0.2s ease; /* Smoother transition */
		top: -9px; /* Position top right */
		right: -9px;
		opacity: 0; /* Hidden by default */
	}

	[type="checkbox"]:checked + label:after {
		opacity: 1; /* Show circle */
	}

	[type="checkbox"]:checked + label:before,
	[type="checkbox"]:not(:checked) + label:before {
		content: "";
		position: absolute;
		border-bottom: 2px solid var(--color-white, white);
		border-right: 2px solid var(--color-white, white);
		transform: rotate(45deg); /* Adjusted rotation for standard check */
		width: 5px; /* Adjusted size */
		height: 10px; /* Adjusted size */
		z-index: 3;
		transition:
			top 0.2s ease,
			right 0.2s ease,
			opacity 0.2s ease; /* Smoother transition */
		top: -7px; /* Position inside the circle */
		right: -3px;
		opacity: 0; /* Hidden by default */
	}

	[type="checkbox"]:checked + label:before {
		opacity: 1; /* Show checkmark */
	}

	/* Disabled state */
	[type="checkbox"]:disabled + label {
		border-color: var(--color-soft);
		background-color: var(--color-light-grey, #f8f8f8); /* Slightly different background */
		ooopacity: 0.7; /* Indicate disabled */
		color: #333 !important; 
		box-shadow: none !important;
		cursor: not-allowed; /* Indicate not interactive */
	}

	[type="checkbox"]:disabled:checked + label {
		background-color: var(--color-soft); 
		border-color: var(--color-border, #ccc);
	}

	[type="checkbox"]:disabled:checked + label:after {
		background-color: var(--color-border, #ccc); /* Muted checkmark circle */
	}
	[type="checkbox"]:disabled:checked + label:before {
		border-color: var(--color-light-grey, #f8f8f8); /* Hide checkmark effectively */
	}
</style>
